import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NutritionProvider } from "@/contexts/NutritionContext";
import React, { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { supabase } from "@/integrations/supabase/client";
import { syncHealthToSupabase } from "@/lib/appleHealth";
import { Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { Auth } from "./pages/Auth";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ResetPassword } from "./pages/ResetPassword";
import { AuthCallback } from "./pages/AuthCallback";
import { Onboarding } from "./pages/Onboarding";
import { TutorialPage } from "./pages/TutorialPage";
import { EducationalSlides } from "./pages/EducationalSlides";
import { TermsAndConditions } from "./pages/TermsAndConditions";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Support } from "./pages/Support";
import { LegalInfo } from "./pages/LegalInfo";
import { Dashboard } from "./pages/Dashboard";
import { Chat } from "./pages/Chat";
import { Workouts } from "./pages/Workouts";
import { GymWorkout } from "./pages/GymWorkout";
import { RunningWorkout } from "./pages/RunningWorkout";
import { RunningStart } from "./pages/RunningStart";
import { RunningActive } from "./pages/RunningActive";
import { Progress } from "./pages/Progress";
import { FoodLog } from "./pages/FoodLog";
import { Recovery } from "./pages/Recovery";
import { Settings } from "./pages/Settings";
import { AppleHealth } from "./pages/AppleHealth";
import { HealthData } from "./pages/HealthData";
import UploadExerciseImages from "./pages/UploadExerciseImages";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = ({ onReady }: { onReady?: () => void }) => {
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  // Apple Health refresh - runs on every app open and every foreground.
  //
  // Three independent triggers, each with the single-flight mutex in
  // syncHealthToSupabase guaranteeing no stampedes:
  //
  //   1. Cold start          - useEffect runs once on mount
  //   2. Auth becomes ready  - if we mounted before the session was hydrated,
  //                            onAuthStateChange catches the SIGNED_IN event
  //   3. Foreground          - Capacitor `appStateChange` (warm start) AND
  //                            DOM `visibilitychange` (WKWebView fallback,
  //                            fires reliably even when Capacitor's listener
  //                            gets registered after the foreground event)
  //
  // We intentionally live at the App.tsx level (not inside ProtectedRoute)
  // so the listeners survive route changes. ProtectedRoute used to host the
  // old useHealthSync hook - that hook now only handles secondary work
  // (e.g. health-data-loaded events).
  const lastForegroundSyncRef = useRef(0);
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") return;
    const FIVE_MINUTES_MS = 5 * 60 * 1000;

    const isOnboarding = () =>
      typeof window !== "undefined" && window.location.pathname.includes("/onboarding");

    const runSync = async (reason: string) => {
      if (isOnboarding()) {
        console.log(`[HealthForegroundSync] skip (${reason}) - onboarding active`);
        return;
      }
      // Coalesce rapid foreground events (e.g. iOS firing both visibility +
      // appStateChange in the same tick) - the mutex would handle it but
      // this avoids the noisy log.
      const now = Date.now();
      if (now - lastForegroundSyncRef.current < 500) return;
      lastForegroundSyncRef.current = now;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        console.log(`[HealthForegroundSync] skip (${reason}) - no session yet`);
        return;
      }
      try {
        console.log(`[HealthForegroundSync] sync trigger=${reason}`);
        await syncHealthToSupabase(session.user.id, { forceSync: true, skipFeedback: true });
      } catch (err) {
        console.warn(`[HealthForegroundSync] sync failed (${reason}):`, err);
      }
    };

    // 1. Cold start
    runSync("cold-start");

    // 2. Auth-ready (in case session arrives after this effect mounts)
    const { data: authSub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        runSync(`auth:${event.toLowerCase()}`);
      }
    });

    // 3a. Capacitor lifecycle (warm start)
    let capacitorListener: { remove: () => Promise<void> } | null = null;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) runSync("appStateChange:active");
    })
      .then((handle) => {
        capacitorListener = handle;
      })
      .catch((err) => console.warn("[HealthForegroundSync] Capacitor listener failed:", err));

    // 3b. DOM visibility (defensive backup - fires even if Capacitor's
    // listener registers too late or fails). WKWebView dispatches this
    // reliably on iOS background/foreground transitions.
    const onVisibility = () => {
      if (document.visibilityState === "visible") runSync("visibilitychange:visible");
    };
    document.addEventListener("visibilitychange", onVisibility);

    // 4. While the app is open in foreground, refresh every 5 minutes.
    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      runSync("foreground-interval:5min");
    }, FIVE_MINUTES_MS);

    return () => {
      authSub?.subscription.unsubscribe();
      capacitorListener?.remove().catch(() => {});
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(intervalId);
    };
  }, []);
  return (
  <div className="bg-background" style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <NutritionProvider>
          <OfflineIndicator />
          <Toaster />
          <Sonner />
          <Routes>
          {/* Public routes (no Layout) */}
          <Route path="/" element={<Navigate to="/auth" replace />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/masterpromptab" element={<LegalInfo />} />
          
          {/* Protected routes without Layout (onboarding flow) */}
          <Route 
            path="/onboarding" 
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tutorial" 
            element={
              <ProtectedRoute>
                <TutorialPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/educational-slides" 
            element={
              <ProtectedRoute>
                <EducationalSlides />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected routes WITH shared Layout */}
          <Route 
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/chat" element={<ErrorBoundary><Chat /></ErrorBoundary>} />
            <Route path="/workouts" element={<Workouts />} />
            <Route path="/gym-workout" element={<GymWorkout />} />
            <Route path="/running-start" element={<RunningStart />} />
            <Route path="/running-workout" element={<RunningWorkout />} />
            <Route path="/running-active" element={<ErrorBoundary><RunningActive /></ErrorBoundary>} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/food-log" element={<FoodLog />} />
            <Route path="/recovery" element={<Recovery />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/apple-health" element={<AppleHealth />} />
            <Route path="/health-data" element={<HealthData />} />
            <Route path="/upload-exercise-images" element={<UploadExerciseImages />} />
          </Route>
          
          {/* Catch-all 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </NutritionProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </div>
  );
};

export default App;